import { DependencyList, useCallback } from 'react'
import {
	ApiGetStreamWithToken,
  ApiGetWithToken,
	ApiPostWithToken,
	useApiFetch,
	useApiPost
} from 'shared/api'
import {
	kojinApiBy,
	searchsKojinApi,
	searchsKojinApiBy,
	searchsSisetuApiBy,
	sisetuApiBy,
	targetListApiBy,
	targetListsKojinApiBy
} from 'interface/endpoints'
import {
	KojinResult,
	KojinSummaryFormData,
	KojinSummaryResult
} from './types'
import { useApiFetchStream } from 'shared/api/apiFetchStream'
import { useUpdateTime } from 'interface/util'

export * from './types'

type useListKojinParams = {
	sisetuCode?: string
	targetListId?: number | null
	mrpProjectId?: number | null
	lvProjectCode?: string | null
	mesh?: string
}
export const useListKojin = (
	searchId: number | undefined | null,
	{ sisetuCode, targetListId, mrpProjectId, lvProjectCode, mesh }: useListKojinParams = {},
	option: {
		updateTime?: string
		rankLimit?: number
		onPreFetch?: () => void
		onFetched?: (result: KojinResult[]) => void
	} = {},
	deps?: DependencyList
) => {
	const { updateTime, rankLimit, onPreFetch, onFetched } = option
	const t = useUpdateTime(updateTime)
	return useApiFetchStream<KojinResult>(
		useCallback(
			async (apiAccess, abort, onInitialize, onLoad) => {
				const result: KojinResult[] = []
				await ApiGetStreamWithToken<KojinResult>(
					apiAccess,
					searchId ? (
						sisetuCode ? searchsSisetuApiBy(searchId, sisetuCode, '/kojin/stream', { t, mesh }) :
						targetListId ? targetListApiBy(searchId, targetListId, '/kojin/stream', { t }) :
						mrpProjectId ? searchsKojinApi(searchId, `/mrp/${mrpProjectId}/stream`, { t }) :
						lvProjectCode ? searchsKojinApi(searchId, `/lv/${lvProjectCode}/stream`, { t }) :
						searchsKojinApi(searchId, '/stream', { t: t, rankLimit })
					) : (
						sisetuCode ? sisetuApiBy(sisetuCode, '/kojin/stream', { mesh }) :
						undefined
					),
					(count) => {
						if (onPreFetch) onPreFetch()
						onInitialize(count)
					},
					async (kojin) => {
						result.push(kojin)
						onLoad(result.length)
					},
					abort?.signal ?? null
				)
				if (onFetched) onFetched(result)
				return result
			},
			[t, searchId, sisetuCode, mesh, targetListId, mrpProjectId, lvProjectCode, rankLimit, onFetched, onPreFetch]
		),
		deps
	)
}

export const useGetKojin = (
	searchId: number | null | undefined,
	targetListId: number | null | undefined,
	kojinCode: string | null | undefined,
	option: {
		defaultKojin?: KojinResult,
		updateTime?: string
	} = {}
) => {
	const { defaultKojin, updateTime } = option
	const t = useUpdateTime(updateTime)
	return useApiFetch(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!kojinCode) return null
				if (defaultKojin && defaultKojin.kojinCode === kojinCode) return defaultKojin
				return ApiGetWithToken<KojinResult>(
					apiAccess,
					searchId ? (
						targetListId ? targetListsKojinApiBy(searchId, targetListId, kojinCode, undefined, { t }) :
						searchsKojinApiBy(searchId, kojinCode, undefined, { t })
					) : kojinApiBy(kojinCode),
					cancelToken
				)
			},
			[t, searchId, targetListId, kojinCode, defaultKojin]
		)
	)
}

export const useGetKojinSummary = (
	searchId: number | null | undefined,
	kojinCode: string | null | undefined,
	option: {
		updateTime?: string
	} = {}
) => {
	const t = useUpdateTime(option.updateTime)
	return useApiFetch(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!searchId || !kojinCode) return null
				return ApiGetWithToken<KojinSummaryResult>(
					apiAccess,
					searchsKojinApiBy(searchId, kojinCode, '/summary', { t }),
					cancelToken
				)
			},
			[t, searchId, kojinCode]
		)
	)
}
export const usePostKojinSummary = (
	searchId: number | undefined | null,
	kojinCode: string | undefined | null,
	callback?: {
		onPosted?: (result: KojinSummaryResult) => void
		onError?: (reason: Error | undefined) => void
	}
) => useApiPost<KojinSummaryFormData, KojinSummaryResult>(
	useCallback(
		(apiAccess, cancelToken, form, webSocketId) => {
			if (!searchId || !kojinCode) return null
			return ApiPostWithToken(
				apiAccess,
				searchsKojinApiBy(searchId, kojinCode, '/summary'),
				form,
				cancelToken,
				webSocketId
			)
		},
		[searchId, kojinCode]
	),
	useCallback(
		(result) => {
			if (callback?.onPosted) callback.onPosted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
			if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const usePostKojinSummaryGenerativeAI = (
	searchId: number | null | undefined,
	kojinCode: string | null | undefined,
	callback?: {
		onPosted?: (result: string | null) => void
		onError?: (reason: Error | undefined) => void
	}
) => useApiPost<void, string | null>(
	useCallback(
		(apiAccess, cancelToken, form, webSocketId) => {
			if (!searchId || !kojinCode) return null
			return ApiPostWithToken(
				apiAccess,
				searchsKojinApiBy(searchId, kojinCode, '/summary/genAI'),
				form,
				cancelToken,
				webSocketId
			)
		},
		[searchId, kojinCode]
	),
	useCallback(
		(result) => {
			if (callback?.onPosted) callback.onPosted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
			if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)
